import { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileBrowser } from '@/components/files/FileBrowser';
import { api, type UserFileResponse } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

export interface FiasImagePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImageSelected: (file: { fileId: string; fileName: string; dataUrl: string }) => void;
}

export function FiasImagePickerDialog({
  open,
  onOpenChange,
  onImageSelected,
}: FiasImagePickerDialogProps) {
  const [selectedFile, setSelectedFile] = useState<UserFileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (isLoading) return;
      if (!next) setSelectedFile(null);
      onOpenChange(next);
    },
    [isLoading, onOpenChange],
  );

  const handleFileSelect = useCallback((file: UserFileResponse) => {
    if (!file.mimeType?.startsWith('image/')) {
      toast({
        title: 'Not an image',
        description: 'Please select an image file.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedFile(file);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    try {
      const { downloadUrl } = await api.getDownloadUrl(selectedFile.fileId);
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      onImageSelected({
        fileId: selectedFile.fileId,
        fileName: selectedFile.fileName,
        dataUrl,
      });
      setSelectedFile(null);
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Failed to load image',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, onImageSelected, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Open Fias image</DialogTitle>
          <DialogDescription>
            Pick an image from your Fias storage to load into the editor.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0">
          <FileBrowser onFileSelect={handleFileSelect} />
        </div>

        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <span className="text-sm text-muted-foreground truncate">
            {selectedFile ? selectedFile.fileName : 'No file selected'}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!selectedFile || isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Open
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
